import { localStorageUser } from './global-constants';

/**
 * Kde leží přihlášení této záložky (EL-2357).
 *
 * Běžně je token v `localStorage` — sdílí ho všechny záložky prohlížeče, takže
 * přihlášení v jedné platí ve všech. **Náhled kurzu jako student** ale běží pod jiným
 * účtem (skrytý náhledový student) a nesmí vydavateli přepsat přihlášení v ostatních
 * záložkách. Záložka náhledu proto drží token v `sessionStorage`, které patří jen jí.
 *
 * Jediné místo, kudy se token čte a zapisuje — `jwt.ts`, `TokenInterceptor`,
 * `AuthGuard`, `AuthFacade` i ruční `fetch` při odchodu ze stránky. Kdo sáhne na
 * `localStorage.getItem(localStorageUser)` napřímo, přečte v záložce náhledu token
 * vydavatele.
 *
 * ⚠ **Bez závislostí, žádná služba.** Čte ho `TokenInterceptor`, a cokoli, co by si
 * vzalo `TranslateService`, by DI zacyklilo (NG0200, `angular-conventions.md` §8b).
 *
 * Záložka zůstane záložkou náhledu, dokud náhled výslovně neukončí
 * (`endPreviewSession`) — i když token vyprší a dojde k novému přihlášení. Kdyby se
 * po vypršení vrátila k `localStorage`, nové přihlášení v ní by přepsalo vydavatele
 * ve všech záložkách.
 */

/** Značka záložky náhledu v `sessionStorage`; nese id kurzu, ze kterého náhled vznikl. */
export const previewSessionKey = 'previewSession';

interface PreviewSession {
  courseId: string;
}

/** Běží v této záložce náhled kurzu? */
export function isPreviewTab(): boolean {
  return readPreviewSession() !== undefined;
}

/** Id kurzu, pro který byl náhled v této záložce otevřen, nebo `undefined`. */
export function previewCourseId(): string | undefined {
  return readPreviewSession()?.courseId;
}

/** Surová hodnota tokenu z úložiště této záložky (JSON řetězec), nebo `null`. */
export function readStoredTokenRaw(): string | null {
  return tokenStorage().getItem(localStorageUser);
}

/** Uloží token přihlášení do úložiště této záložky. */
export function storeToken(token: string): void {
  tokenStorage().setItem(localStorageUser, JSON.stringify(token));
}

/** Smaže token z úložiště této záložky — v záložce náhledu jen token náhledu. */
export function clearStoredToken(): void {
  tokenStorage().removeItem(localStorageUser);
}

/**
 * Z této záložky udělá záložku náhledu a uloží do ní token náhledového studenta.
 * `localStorage` (přihlášení vydavatele) zůstane netknuté.
 */
export function startPreviewSession(token: string, courseId: string): void {
  const session: PreviewSession = { courseId };

  sessionStorage.setItem(previewSessionKey, JSON.stringify(session));
  sessionStorage.setItem(localStorageUser, JSON.stringify(token));
}

/** Ukončí náhled v této záložce — po něm se záložka znovu řídí přihlášením z `localStorage`. */
export function endPreviewSession(): void {
  sessionStorage.removeItem(localStorageUser);
  sessionStorage.removeItem(previewSessionKey);
}

function tokenStorage(): Storage {
  return isPreviewTab() ? sessionStorage : localStorage;
}

function readPreviewSession(): PreviewSession | undefined {
  const raw = sessionStorage.getItem(previewSessionKey);

  if (!raw) {
    return undefined;
  }

  try {
    const session = JSON.parse(raw);

    return session && typeof session.courseId === 'string' ? session : undefined;
  } catch {
    return undefined;
  }
}
